/**
 * Notification History Hook
 *
 * On-demand, paginated read of older notifications for the history modal.
 * Uses getDocs + startAfter (no live listener), so history costs nothing
 * while closed and never feeds the bell's unread-to-toast sync.
 *
 * Items pass the same visibility rule as the bell
 * (utils/notificationVisibility). Because that filtering happens in the
 * browser, one "Load older" may fetch several raw pages.
 *
 * Two sources are paginated independently (broadcast `notifications` by
 * location, personal `userNotifications/{email}/items`) and merged by
 * timestamp with a k-way merge (utils/notificationMerge: loadHistoryPage),
 * so a newer item never appears after an older one across page boundaries.
 */

import { useState, useRef, useCallback } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  getDocs,
  type QueryConstraint,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { useFirebaseStore } from '../store/firebaseStore';
import type { NotificationItem } from '@/types/notification.types';
import { getUserNotificationsCollectionPath } from '../config/collections';
import { logger } from '../utils/logger';
import { NOTIFICATION_TYPE_REGISTRY, isPersonalNotificationType } from '../config/notificationRegistry';
import { getNotificationQueryLocations, isNotificationVisibleToUser } from '../utils/notificationVisibility';
import {
  HISTORY_MAX_FETCHES_PER_LOAD,
  HISTORY_PAGE_SIZE,
  mergeHistoryItems,
  parseNotificationDoc,
  parsePersonalNotificationDoc,
} from '../utils/notificationHistory';
import {
  createHistoryStates,
  historyHasMore,
  loadHistoryPage,
  type HistoryFetchResult,
  type HistorySourceKey,
  type HistoryStates,
} from '../utils/notificationMerge';

/** Whether any personal type is registered (otherwise the personal source can never hold anything). */
const hasPersonalNotificationTypes = (): boolean =>
  Object.keys(NOTIFICATION_TYPE_REGISTRY).some(isPersonalNotificationType);

export const useNotificationHistory = () => {
  const { db, selectedLocation, currentUser } = useFirebaseStore();
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);

  const statesRef = useRef<HistoryStates | null>(null);
  const cursorsRef = useRef<Record<HistorySourceKey, QueryDocumentSnapshot | null>>({
    broadcast: null,
    personal: null,
  });
  // Bumped on every reset so a slow in-flight load can't write into a newer view
  const generationRef = useRef(0);
  const loadingRef = useRef(false);

  const loadPage = useCallback(
    async (reset: boolean) => {
      if (!db || !currentUser) return;
      if (loadingRef.current && !reset) return;

      const locations = getNotificationQueryLocations(currentUser, selectedLocation);

      if (reset) {
        generationRef.current += 1;
        cursorsRef.current = { broadcast: null, personal: null };
        // No broadcast locations: the broadcast source is simply empty (personal still loads)
        // No registered personal type: nothing can live there, so skip the read entirely
        statesRef.current = createHistoryStates({ broadcast: !locations, personal: !hasPersonalNotificationTypes() });
        setItems([]);
        setHasMore(false);
      }
      if (!statesRef.current) return;
      const generation = generationRef.current;

      loadingRef.current = true;
      setIsLoading(true);
      setError(null);

      const personalPath = getUserNotificationsCollectionPath(currentUser.email);

      // Cursors are local to this load; they're committed with the states only if
      // this generation is still current (a stale load must never touch the new view's cursors).
      const cursors = { ...cursorsRef.current };
      const isStale = () => generation !== generationRef.current;

      const fetchRawPage = async (source: HistorySourceKey): Promise<HistoryFetchResult> => {
        const constraints: QueryConstraint[] = [];
        if (source === 'broadcast') constraints.push(where('locationId', 'in', locations ?? []));
        constraints.push(orderBy('timestamp', 'desc'));
        const cursor = cursors[source];
        if (cursor) constraints.push(startAfter(cursor));
        constraints.push(limit(HISTORY_PAGE_SIZE));

        const collectionPath = source === 'broadcast' ? 'notifications' : personalPath;
        const snapshot = await getDocs(query(collection(db, collectionPath), ...constraints));
        logger.firestore(
          'Read',
          source === 'broadcast'
            ? `notifications (history, ${snapshot.size} docs, Locs: ${(locations ?? []).join(',')})`
            : `${personalPath} (history, ${snapshot.size} docs)`
        );

        const visible: NotificationItem[] = [];
        let frontier: number | null = null;
        for (const docSnapshot of snapshot.docs) {
          const item =
            source === 'broadcast'
              ? parseNotificationDoc(docSnapshot.id, docSnapshot.data())
              : parsePersonalNotificationDoc(docSnapshot.id, docSnapshot.data(), currentUser.email);
          frontier = item.timestamp;
          if (isNotificationVisibleToUser(item, currentUser)) visible.push(item);
        }
        if (snapshot.docs.length > 0) cursors[source] = snapshot.docs[snapshot.docs.length - 1];

        return { items: visible, frontier, exhausted: snapshot.docs.length < HISTORY_PAGE_SIZE };
      };

      const fetchPage = async (source: HistorySourceKey): Promise<HistoryFetchResult> => {
        if (isStale()) return { items: [], frontier: null, exhausted: true };
        if (source === 'broadcast') return fetchRawPage(source);
        // A failing personal source must not blank broadcast history: treat it as exhausted
        try {
          return await fetchRawPage(source);
        } catch (err) {
          logger.error('[NotificationHistory] Failed to load personal history:', err);
          return { items: [], frontier: null, exhausted: true };
        }
      };

      try {
        const result = await loadHistoryPage(statesRef.current, fetchPage, {
          target: HISTORY_PAGE_SIZE,
          // Per-source budget (matches the single-source cap this replaced)
          maxFetches: HISTORY_MAX_FETCHES_PER_LOAD * 2,
        });

        if (isStale()) return;
        statesRef.current = result.states;
        cursorsRef.current = cursors;
        setItems((prev) => mergeHistoryItems(prev, result.emitted));
        setHasMore(historyHasMore(result.states));
      } catch (err) {
        if (generation !== generationRef.current) return;
        logger.error('[NotificationHistory] Failed to load history:', err);
        setError('Failed to load notification history');
      } finally {
        if (generation === generationRef.current) {
          loadingRef.current = false;
          setIsLoading(false);
        }
      }
    },
    [db, currentUser, selectedLocation]
  );

  /** Clear and load the newest page (call on open / refresh). */
  const refresh = useCallback(() => loadPage(true), [loadPage]);

  /** Append the next page of older notifications. */
  const loadOlder = useCallback(() => loadPage(false), [loadPage]);

  return { items, isLoading, error, hasMore, refresh, loadOlder };
};
